import React, { useEffect, useState } from "react";
import api from "../../api/axiosClient";
import { Link } from "react-router-dom";

interface OutsourceRole {
  id: number;
  roleName: string;
}

export default function OutsourceRoles() {
  const [roles, setRoles] = useState<OutsourceRole[]>([]);
  const [roleName, setRoleName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Load all roles
  const loadRoles = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get<OutsourceRole[]>("/OutsourceRole");
      setRoles(res.data);
    } catch (err) {
      setError("Failed to load roles. Please check the API connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  // ✅ Add a new role
  const addRole = async () => {
    if (!roleName.trim()) {
      setError("Please enter a role name.");
      return;
    }
    try {
      setLoading(true);
      await api.post("/OutsourceRole", { roleName });
      setRoleName("");
      loadRoles();
    } catch (err) {
      setError("Failed to add role. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete role
  const deleteRole = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;
    try {
      setLoading(true);
      await api.delete(`/OutsourceRole/${id}`);
      setRoles((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError("Failed to delete role.");
    } finally {
      setLoading(false);
    }
  };

  return (
    
    <div className="p-6 max-w-2xl mx-auto">
        
      {/* Navigation */}
      <div className="flex items-center space-x-2 mb-4">
        <Link 
          to="/admin/dashboard" 
          className="text-blue-500 hover:text-blue-700"
        >
          Dashboard
        </Link>
        <span className="text-gray-500">/</span>
        <span className="text-gray-700">Manage Outsource Roles</span>
      </div>

      

      {/* Rest of your existing code... */}
    
      <h2 className="text-2xl font-semibold mb-4">Manage Outsource Roles</h2>

      {/* Add Role Section */}
      <div className="flex space-x-2 mb-6">
        <input
          type="text"
          placeholder="Enter new role name"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          className="border border-gray-300 rounded px-3 py-2 flex-grow"
        />
        <button
          onClick={addRole}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded disabled:bg-gray-400"
        >
          {loading ? "Adding..." : "Add Role"}
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Roles List */}
      {loading ? (
        <p>Loading...</p>
      ) : roles.length === 0 ? (
        <p className="text-gray-500">No roles found.</p>
      ) : (
        <table className="w-full border border-gray-200 bg-white rounded-lg shadow-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Role Name</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2">{role.roleName}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => deleteRole(role.id)}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
